import React, { useState, useEffect } from 'react';
import axios from 'axios';
import '../../styles/MedicationToday.css'; 
import { useAuth } from '../../hooks/useAuth';

const MedicationToday = ({ admissionId }) => {
  const [medications, setMedications] = useState([]);
  const [canceledMedications, setCanceledMedications] = useState([]);
  const [dueOverdueMedications, setDueOverdueMedications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [selectedMedication, setSelectedMedication] = useState(null);
  const [reason, setReason] = useState('');
  const [reasonText, setReasonText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [expandedMedications, setExpandedMedications] = useState(new Set());
  const isMounted = React.useRef(true);
  const { hasPermission } = useAuth();

  const fetchMedications = async () => {
    try {
      // Fetch administered medications
      const administeredResponse = await axios.get(
        `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/admissions/medication-chart/today/${admissionId}`
      );
      
      // Fetch canceled medications (notGiven=true) for today
      const canceledResponse = await axios.get(
        `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/admissions/medication-chart/canceled-today/${admissionId}`
      );
      
      // Fetch due/overdue medications
      const dueOverdueResponse = await axios.get(
        `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/admissions/medication-chart/due-overdue/${admissionId}`
      );
      
      if (isMounted.current) {
        // Group administered medications by name and dosage
        const groupedAdministered = administeredResponse.data.reduce((acc, med) => {
          const key = `${med.AdmissionMedicationOrder.medication_name} ${med.AdmissionMedicationOrder.dosage}`;
          if (!acc[key]) {
            acc[key] = {
              name: med.AdmissionMedicationOrder.medication_name,
              dosage: med.AdmissionMedicationOrder.dosage,
              route: med.AdmissionMedicationOrder.route,
              count: 1
            };
          } else {
            acc[key].count++;
          }
          return acc;
        }, {});

        // Group canceled medications by name and dosage
        const groupedCanceled = canceledResponse.data.reduce((acc, med) => {
          const key = `${med.AdmissionMedicationOrder.medication_name} ${med.AdmissionMedicationOrder.dosage}`;
          if (!acc[key]) {
            acc[key] = {
              name: med.AdmissionMedicationOrder.medication_name,
              dosage: med.AdmissionMedicationOrder.dosage,
              route: med.AdmissionMedicationOrder.route,
              reason: med.reason,
              count: 1
            };
          } else {
            acc[key].count++;
          }
          return acc;
        }, {});

        setMedications(Object.values(groupedAdministered));
        setCanceledMedications(Object.values(groupedCanceled));
        setDueOverdueMedications(dueOverdueResponse.data);
        setLoading(false);
      }
    } catch (err) {
      console.error('Error fetching medications:', err);
      if (isMounted.current) {
        setError('Failed to load medications');
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (admissionId) {
      fetchMedications();
      
      const intervalId = setInterval(() => {
        if (isMounted.current) {
          fetchMedications();
        }
      }, 2 * 60 * 1000); // 2 minutes for more frequent updates

      return () => {
        isMounted.current = false;
        clearInterval(intervalId);
      };
    }
  }, [admissionId]);

  const formatTime = (timeString) => {
    const [hours, minutes] = timeString.split(':');
    const hour12 = hours % 12 || 12;
    const ampm = hours >= 12 ? 'PM' : 'AM';
    return `${hour12}:${minutes} ${ampm}`;
  };

  const handleMarkNotGiven = (medication) => {
    setSelectedMedication(medication);
    setReason('');
    setReasonText('');
    setShowReasonModal(true);
  };

  const handleSubmitReason = async () => {
    if (!reason) {
      alert('Please select a reason');
      return;
    }
    
    if (reason === 'Other' && !reasonText.trim()) {
      alert('Please provide a reason text');
      return;
    }

    setSubmitting(true);
    try {
      await axios.put(
        `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/admissions/medication-chart/mark-not-given/${selectedMedication.log_id}`,
        {
          reason: reason,
          reasonText: reason === 'Other' ? reasonText : null
        }
      );
      
      // Refresh medications after marking as not given
      await fetchMedications();
      setShowReasonModal(false);
      setSelectedMedication(null);
    } catch (err) {
      console.error('Error marking medication as not given:', err);
      alert('Failed to mark medication as not given');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setShowReasonModal(false);
    setSelectedMedication(null);
    setReason('');
    setReasonText('');
  };

  const toggleMedicationDetails = (medicationKey) => {
    setExpandedMedications(prev => {
      const newSet = new Set(prev);
      if (newSet.has(medicationKey)) {
        newSet.delete(medicationKey);
      } else {
        newSet.add(medicationKey);
      }
      return newSet;
    });
  };

  if (loading) return <div>Loading medications...</div>;
  if (error) return <div className="error">{error}</div>;

  const hasDueOverdue = dueOverdueMedications.length > 0;
  
  // Group medications by status
  const overdueMedications = dueOverdueMedications.filter(med => med.status === 'overdue');
  const dueMedications = dueOverdueMedications.filter(med => med.status === 'due');

  return (
    <div className="medication-today-widget">
      <h4>Today's Medications</h4>
      
      {/* Due/Overdue Medications Section */}
      {hasDueOverdue && (
        <div className="medications-section urgent-section">
          <h5 className="section-title urgent">⚠️ Action Required</h5>
          
          {/* Overdue Medications */}
          {overdueMedications.length > 0 && (
            <>
              <div className="status-header overdue">OVERDUE</div>
              <ul className="medication-list urgent-list">
                {overdueMedications.map((med, index) => {
                  const medicationKey = `overdue-${index}`;
                  const isExpanded = expandedMedications.has(medicationKey);
                  return (
                    <li key={medicationKey} className="medication-item urgent overdue">
                      <div 
                        className={`medication-details ${isExpanded ? 'expanded' : 'collapsed'}`}
                        onClick={() => toggleMedicationDetails(medicationKey)}
                      >
                        <span className={`med-name ${isExpanded ? 'compressed' : 'full'}`}>
                          {med.AdmissionMedicationOrder.medication_name}
                        </span>
                        <span className="expand-indicator">{isExpanded ? '◀' : '▶'}</span>
                        <div className={`med-details ${isExpanded ? 'visible' : 'hidden'}`}>
                          <span className="med-dosage">{med.AdmissionMedicationOrder.dosage}</span>
                          <span className="med-route">{med.AdmissionMedicationOrder.route}</span>
                          <span className="scheduled-time">@ {formatTime(med.scheduled_time)}</span>
                          {hasPermission('/admission-card/change-medication-schedule', 'Edit') && (
                            <button 
                              className="action-button" 
                              onClick={(e) => {
                                e.stopPropagation();
                                handleMarkNotGiven(med);
                              }}
                              title="Mark as not given"
                            >
                              ✕
                            </button>
                          )}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
          
          {/* Due Now Medications */}
          {dueMedications.length > 0 && (
            <>
              <div className="status-header due">DUE NOW</div>
              <ul className="medication-list urgent-list">
                {dueMedications.map((med, index) => {
                  const medicationKey = `due-${index}`;
                  const isExpanded = expandedMedications.has(medicationKey);
                  return (
                    <li key={medicationKey} className="medication-item urgent due">
                      <div 
                        className={`medication-details ${isExpanded ? 'expanded' : 'collapsed'}`}
                        onClick={() => toggleMedicationDetails(medicationKey)}
                      >
                        <span className={`med-name ${isExpanded ? 'compressed' : 'full'}`}>
                          {med.AdmissionMedicationOrder.medication_name}
                        </span>
                        <span className="expand-indicator">{isExpanded ? '◀' : '▶'}</span>
                        <div className={`med-details ${isExpanded ? 'visible' : 'hidden'}`}>
                          <span className="med-dosage">{med.AdmissionMedicationOrder.dosage}</span>
                          <span className="med-route">{med.AdmissionMedicationOrder.route}</span>
                          <span className="scheduled-time">@ {formatTime(med.scheduled_time)}</span>
                          {hasPermission('/admission-card/change-medication-schedule', 'Edit') && (
                            <button 
                              className="action-button" 
                              onClick={(e) => {
                                e.stopPropagation();
                                handleMarkNotGiven(med);
                              }}
                              title="Mark as not given"
                            >
                              ✕
                            </button>
                          )}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </div>
      )}

      {/* Administered Medications Section */}
      <div className="medications-section administered-section">
        <h5 className="section-title">✅ Administered Today</h5>
        {medications.length === 0 ? (
          <p className="no-medications">No medications administered today</p>
        ) : (
          <ul className="medication-list administered-list">
            {medications.map((med, index) => {
              const medicationKey = `administered-${index}`;
              const isExpanded = expandedMedications.has(medicationKey);
              return (
                <li key={medicationKey} className="medication-item administered">
                  <div 
                    className={`medication-details ${isExpanded ? 'expanded' : 'collapsed'}`}
                    onClick={() => toggleMedicationDetails(medicationKey)}
                  >
                    <span className={`med-name ${isExpanded ? 'compressed' : 'full'}`}>
                      {med.name}
                    </span>
                    <span className="expand-indicator">{isExpanded ? '◀' : '▶'}</span>
                    <div className={`med-details ${isExpanded ? 'visible' : 'hidden'}`}>
                      <span className="med-dosage">{med.dosage}</span>
                      <span className="med-count">({med.count}x)</span>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {/* Canceled Medications Sub-section */}
        {canceledMedications.length > 0 && (
          <>
            <h6 className="section-subtitle">❌ Canceled Today</h6>
            <ul className="medication-list canceled-list">
              {canceledMedications.map((med, index) => {
                const medicationKey = `canceled-${index}`;
                const isExpanded = expandedMedications.has(medicationKey);
                return (
                  <li key={medicationKey} className="medication-item canceled">
                    <div 
                      className={`medication-details ${isExpanded ? 'expanded' : 'collapsed'}`}
                      onClick={() => toggleMedicationDetails(medicationKey)}
                    >
                      <span className={`med-name ${isExpanded ? 'compressed' : 'full'}`}>
                        {med.name}
                      </span>
                      <span className="expand-indicator">{isExpanded ? '◀' : '▶'}</span>
                      <div className={`med-details ${isExpanded ? 'visible' : 'hidden'}`}>
                        <span className="med-dosage">{med.dosage}</span>
                        <span className="med-count">({med.count}x)</span>
                        <span className="cancel-reason">{med.reason}</span>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </div>

      {/* Reason Modal */}
      {showReasonModal && (
        <div className="med-modal-overlay" onClick={handleCloseModal}>
          <div className="med-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="med-modal-header">
              <h6>Mark Medication as Not Given</h6>
              <button className="med-close-button" onClick={handleCloseModal}>×</button>
            </div>
            <div className="med-modal-body">
              <div className="med-form-group">
                <label htmlFor="reason">Reason:</label>
                <select 
                  id="reason"
                  value={reason} 
                  onChange={(e) => setReason(e.target.value)}
                  className="med-form-select"
                >
                  <option value="">Select a reason</option>
                  <option value="Before Admission Time">Before Admission</option>
                  <option value="Instruction from Doctor">Doctor Instruction</option>
                  <option value="Medicine Unavailable">Medicine Unavailable</option>
                  <option value="Patient Refused">Patient Refused</option>
                  <option value="After Discharge Time">After Discharge</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              
              {reason === 'Other' && (
                <div className="med-form-group">
                  <label htmlFor="reasonText">Please specify:</label>
                  <textarea
                    id="reasonText"
                    value={reasonText}
                    onChange={(e) => setReasonText(e.target.value)}
                    className="med-form-textarea"
                    placeholder="Enter reason..."
                    rows="3"
                  />
                </div>
              )}
            </div>
            <div className="med-modal-footer">
              <button 
                className="med-btn-cancel" 
                onClick={handleCloseModal}
                disabled={submitting}
              >
                Cancel
              </button>
              <button 
                className="med-btn-submit" 
                onClick={handleSubmitReason}
                disabled={submitting}
              >
                {submitting ? 'Submitting...' : 'Submit'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MedicationToday;
